import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService, availabilityAPI } from '../services/api';
import NavigationBar from './NavigationBar';
import EventNavigation from './EventNavigation';
import LoadingSpinner from './LoadingSpinner';
import './EventHome.css';
import './EventAvailability.css';

const EventAvailability = () => {
    const { eventNameId } = useParams();
    const navigate = useNavigate();
    const [event, setEvent] = useState(null);
    const [availability, setAvailability] = useState(null);
    const [loading, setLoading] = useState(true);
    const [availabilityLoading, setAvailabilityLoading] = useState(true);
    const [error, setError] = useState('');
    const [availabilityError, setAvailabilityError] = useState('');

    useEffect(() => {
        if (eventNameId) {
            localStorage.setItem('last_event', eventNameId);
            fetchEventData();
            fetchAvailability();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventNameId]);

    const fetchEventData = async () => {
        try {
            setLoading(true);
            const response = await apiService.getEventByNameId(eventNameId);
            setEvent(response.data);
        } catch (err) {
            console.error('Error fetching event:', err);
            setError('Failed to load event details');
        } finally {
            setLoading(false);
        }
    };

    const fetchAvailability = async () => {
        try {
            setAvailabilityLoading(true);
            const response = await availabilityAPI.getAvailability(eventNameId);
            setAvailability(response.data.availability);
        } catch (err) {
            console.error('Error fetching availability:', err);
            setAvailabilityError(err.response?.data?.detail || 'Failed to load availability');
        } finally {
            setAvailabilityLoading(false);
        }
    };

    const handleToggleAvailability = async (meetingTimeId, currentAvailable) => {
        // Optimistically update local state
        setAvailability(prev => {
            const updated = { ...prev };
            updated.eventAvailabilityScheduleItems = updated.eventAvailabilityScheduleItems.map(day => ({
                ...day,
                meetingTimes: day.meetingTimes.map(mt =>
                    mt.meetingTimeId === meetingTimeId
                        ? { ...mt, available: !currentAvailable }
                        : mt
                )
            }));
            return updated;
        });

        try {
            // Call API to toggle availability using ic_event_id
            await availabilityAPI.toggleAvailability(event.ic_event_id, meetingTimeId);
        } catch (err) {
            console.error('Error toggling availability:', err);
            // Revert on error
            setAvailability(prev => {
                const updated = { ...prev };
                updated.eventAvailabilityScheduleItems = updated.eventAvailabilityScheduleItems.map(day => ({
                    ...day,
                    meetingTimes: day.meetingTimes.map(mt =>
                        mt.meetingTimeId === meetingTimeId
                            ? { ...mt, available: currentAvailable }
                            : mt
                    )
                }));
                return updated;
            });
        }
    };

    // Get Wednesday, February 25 schedule
    const wednesdaySchedule = availability?.eventAvailabilityScheduleItems?.find(
        item => item.date === "Wednesday, February 25"
    );

    if (loading) {
        return (
            <>
                <NavigationBar />
                <div className="event-layout">
                    <EventNavigation eventNameId={eventNameId} />
                    <div className="event-home-container with-sidebar">
                        <LoadingSpinner message="Loading availability..." />
                    </div>
                </div>
            </>
        );
    }

    if (error || !event) {
        return (
            <>
                <NavigationBar />
                <div className="event-layout">
                    <EventNavigation eventNameId={eventNameId} />
                    <div className="event-home-container with-sidebar">
                        <div className="error-message">
                            <h2>Error</h2>
                            <p>{error || 'Event not found'}</p>
                            <button onClick={() => navigate('/login')} className="btn btn-primary">
                                Return to Login
                            </button>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <NavigationBar />
            <div className="event-layout">
                <EventNavigation eventNameId={eventNameId} />
                <div className="event-home-container with-sidebar">
                    <div className="event-content">

                        {availabilityLoading ? (
                            <div className="availability-loading">
                                <LoadingSpinner message="Loading your availability schedule..." />
                            </div>
                        ) : availabilityError ? (
                            <div className="availability-error">
                                <p>{availabilityError}</p>
                            </div>
                        ) : wednesdaySchedule ? (
                            <div className="availability-schedule">
                                <div className="schedule-header">
                                    <h2>{wednesdaySchedule.date}</h2>
                                    {availability?.availabilityDescription && (
                                        <p
                                            className="schedule-description"
                                            dangerouslySetInnerHTML={{
                                                __html: availability.availabilityDescription
                                            }}
                                        />
                                    )}
                                </div>

                                <div className="time-slots-grid">
                                    {wednesdaySchedule.meetingTimes.map((slot) => (
                                        <div
                                            key={slot.meetingTimeId}
                                            className={`time-slot ${slot.available ? 'available' : 'unavailable'}`}
                                        >
                                            <div className="time-slot-time">
                                                {slot.time}
                                            </div>
                                            <div className="time-slot-toggle-group">
                                                <div className="time-slot-status">
                                                    {slot.available ? 'Available' : 'Unavailable'}
                                                </div>
                                                <label className="toggle-switch">
                                                    <input
                                                        type="checkbox"
                                                        checked={slot.available}
                                                        onChange={() => handleToggleAvailability(slot.meetingTimeId, slot.available)}
                                                    />
                                                    <span className="toggle-slider"></span>
                                                </label>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="no-availability">
                                <p>No availability schedule found for Wednesday, February 25.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default EventAvailability;
